import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, rows, type HolidayCalendar, type ID } from '@hrms/shared';
import { api } from '../../../lib/api';
import { fieldErrors } from '../../../lib/errors';
import { useBreakpoint } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { SideMenu } from '../components/misc';
import { Badge, Button, Card, Checkbox, ConfirmDialog, DataTable, DateField, EmptyState, IconButton, Loading, Modal, PageHeader, Row, Screen, Stack, TextField } from '../../../ui';

/** Buttons default to alignSelf flex-start; bottom-align them with labelled fields. */
const END = { alignSelf: 'flex-end' as const };

/** Holiday calendars and their holidays (assigned to locations under Org structure). */
export function HolidaysScreen() {
  const { isTablet } = useBreakpoint();
  const cals = useQuery({ queryKey: ['holiday-calendars', 'list'], queryFn: () => api.leave.calendars.list({ per_page: 200 }) });
  const list = rows(cals.data);
  const [sel, setSel] = useState<ID | null>(null);
  useEffect(() => {
    if ((!sel || !list.some((c) => c.id === sel)) && list.length) setSel(list[0].id);
  }, [list, sel]);
  const cal = useQuery({ queryKey: ['holiday-calendars', 'one', sel], queryFn: () => api.leave.calendars.get(sel!), enabled: !!sel });
  const [edit, setEdit] = useState<Partial<HolidayCalendar> | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [h, setH] = useState({ date: '', name: '', is_optional: false });
  const [delCal, setDelCal] = useState(false);
  const inv = { invalidate: [['holiday-calendars'], ['holidays']] };
  const saveCal = useMut(() => (edit!.id ? api.leave.calendars.update(edit!.id, edit!) : api.leave.calendars.create(edit!)), {
    ...inv,
    success: 'Calendar saved',
    onSuccess: (r) => {
      setEdit(null);
      setSel(r.id);
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  const removeCal = useMut(() => api.leave.calendars.remove(sel!), { ...inv, success: 'Calendar deleted', onSuccess: () => { setSel(null); setDelCal(false); } });
  const add = useMut(() => api.leave.addHoliday(sel!, h), { ...inv, success: 'Holiday added', onSuccess: () => { setH({ date: '', name: '', is_optional: false }); setErrs({}); }, onError: (e) => setErrs(fieldErrors(e)) });
  const del = useMut((id: ID) => api.leave.deleteHoliday(id), { ...inv, success: 'Holiday removed' });
  const c = cal.data;
  const hols = [...(c?.holidays ?? [])].sort((a, b) => a.date.localeCompare(b.date));

  const menu = (
    <SideMenu
      title="Calendars"
      loading={cals.isLoading}
      emptyTitle="No calendars"
      value={sel}
      onChange={setSel}
      items={list.map((x) => ({ key: x.id, title: x.name, right: <Badge>{String(x.year)}</Badge> }))}
      testID="holiday-calendars"
    />
  );
  const detail = c ? (
    <Stack>
      <Card
        title={`${c.name} · ${c.year}`}
        subtitle={`${hols.length} holidays · employees may choose up to ${c.max_optional} optional`}
        actions={
          <>
            <Button size="sm" icon="edit" title="Edit" onPress={() => { setEdit(c); setErrs({}); }} />
            <IconButton icon="trash" size="sm" label="Delete calendar" onPress={() => setDelCal(true)} />
          </>
        }
      >
        <Row align="flex-end" gap={10}>
          <View style={{ minWidth: 170 }}>
            <DateField label="Date" error={errs.date} value={h.date} min={`${c.year}-01-01`} max={`${c.year}-12-31`} onChange={(date) => setH({ ...h, date })} testID="holiday-date" />
          </View>
          <TextField containerStyle={{ flexGrow: 1, flexBasis: 180 }} label="Name" error={errs.name} value={h.name} placeholder="e.g. Diwali" onChangeText={(name) => setH({ ...h, name })} testID="holiday-name" />
          <View style={{ paddingBottom: 10 }}>
            <Checkbox label="Optional" value={h.is_optional} onChange={(is_optional) => setH({ ...h, is_optional })} />
          </View>
          <Button variant="primary" icon="plus" title="Add" loading={add.isPending} disabled={!h.date || !h.name.trim()} onPress={() => add.mutate(undefined)} testID="holiday-add" style={END} />
        </Row>
      </Card>
      <DataTable
        rows={hols}
        emptyTitle="No holidays added"
        columns={[
          { key: 'date', title: 'Date', width: 110, render: (x) => fmtDate(x.date), sortValue: (x) => x.date },
          { key: 'day', title: 'Day', width: 100, sortable: false, render: (x) => new Date(`${x.date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long' }) },
          { key: 'name', title: 'Holiday', width: 180, flex: 2 },
          { key: 'is_optional', title: 'Type', width: 110, sortValue: (x) => (x.is_optional ? 1 : 0), render: (x) => (x.is_optional ? <Badge tone="info">Optional</Badge> : <Badge tone="success">Mandatory</Badge>) },
          { key: 'act', title: '', width: 56, flex: 0, sortable: false, render: (x) => <IconButton icon="trash" size="sm" label={`Remove ${x.name}`} onPress={() => del.mutate(x.id)} /> },
        ]}
      />
    </Stack>
  ) : sel ? (
    <Loading />
  ) : (
    <Card>
      <EmptyState icon="calendar" title="Select or create a calendar" />
    </Card>
  );

  return (
    <Screen testID="holidays">
      <PageHeader
        title="Holiday calendars"
        subtitle="Assign calendars to locations under Org structure → Locations."
        actions={<Button variant="primary" icon="plus" title="New calendar" onPress={() => { setEdit({ year: new Date().getFullYear(), max_optional: 2, name: '' }); setErrs({}); }} testID="calendar-new" />}
      />
      {isTablet ? (
        <View style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
          <View style={{ width: 260 }}>{menu}</View>
          <View style={{ flex: 1, minWidth: 0 }}>{detail}</View>
        </View>
      ) : (
        <Stack>
          {menu}
          {detail}
        </Stack>
      )}
      <Modal
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit?.id ? 'Edit calendar' : 'New holiday calendar'}
        width={460}
        footer={
          <>
            <Button title="Cancel" onPress={() => setEdit(null)} />
            <Button variant="primary" title="Save" loading={saveCal.isPending} disabled={!edit?.name?.trim() || !edit?.year} onPress={() => saveCal.mutate(undefined)} testID="calendar-save" />
          </>
        }
      >
        <TextField label="Name" required error={errs.name} value={edit?.name ?? ''} placeholder="e.g. Karnataka 2026" onChangeText={(name) => setEdit({ ...edit, name })} />
        <TextField label="Year" required keyboardType="number-pad" error={errs.year} value={edit?.year ? String(edit.year) : ''} onChangeText={(v) => setEdit({ ...edit, year: Number(v.replace(/\D/g, '').slice(0, 4)) || undefined })} />
        <TextField label="Max optional holidays per employee" keyboardType="number-pad" error={errs.max_optional} value={String(edit?.max_optional ?? 0)} onChangeText={(v) => setEdit({ ...edit, max_optional: Number(v.replace(/\D/g, '')) || 0 })} />
      </Modal>
      <ConfirmDialog open={delCal} onClose={() => setDelCal(false)} onConfirm={() => removeCal.mutate(undefined)} loading={removeCal.isPending} title="Delete calendar?" message="All holidays in this calendar will be removed." danger confirmLabel="Delete" />
    </Screen>
  );
}
